/**
 * UserInvitationV2 — Accept user invitation page (V2 redesign)
 *
 * Simple flow:
 * 1. Show loading spinner while validating token
 * 2. On success: auto-login and redirect to dashboard
 * 3. On error: show error message with link to login
 */
import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter
} from './ui/dialog';
import { Loader2, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { I18nContext } from '../context/translations/TranslationsContext.context';
import { ArcaContext } from '../context/ArcaContext.context';
import acceptUserInvitation from '../api/users/acceptUserInvitation';
import resolveRealCompanyId from '../utils/resolveRealCompanyId';

const UserInvitationV2: React.FC = () => {
	const navigate = useNavigate();
	const { setToken, setCompanyId, setUserId, setUserAccountType, setLanguage } =
		useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);

	const [loading, setLoading] = useState(true);
	const [isError, setIsError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [showDialog, setShowDialog] = useState(false);

	const invitationToken = new URLSearchParams(window.location.search).get('token') || '';

	useEffect(() => {
		const handleLoginSuccess = (responseData: any) => {
			if (responseData.user) {
				// 🔴 Sin empresa, ni `"null"` ni nada: la clave no existe. `setItem` con `null`
				// guardaba la cadena, truthy al recargar (product#85).
				const companyId = resolveRealCompanyId(responseData.user.companyId);
				setCompanyId(companyId ?? '');
				if (companyId) localStorage.setItem('companyId', companyId);
				else localStorage.removeItem('companyId');

				const userId = responseData.user.id;
				setUserId(userId);
				localStorage.setItem('userId', userId);

				const userAccountType = responseData.user.accountType;
				setUserAccountType(userAccountType);
				localStorage.setItem('userAccountType', userAccountType);

				const language = responseData.user.language;
				setLanguage(language);
				localStorage.setItem('language', language);
			}

			if (responseData.token) {
				localStorage.setItem('token', responseData.token);
				setToken(responseData.token);
			}
		};

		const acceptInvitation = async () => {
			if (!invitationToken) {
				setLoading(false);
				setIsError(true);
				setErrorMessage(translate('user.userInvitation.invitationIncorrectOrExpired'));
				setShowDialog(true);
				return;
			}

			try {
				const res = await acceptUserInvitation(invitationToken);

				if (!res.success) {
					setLoading(false);
					setIsError(true);

					if (res.message === 'INVALID_TOKEN') {
						setErrorMessage(translate('user.userInvitation.invitationIncorrectOrExpired'));
					} else {
						setErrorMessage(translate('user.userInvitation.genericError'));
					}
					setShowDialog(true);
				} else {
					setLoading(false);
					setIsError(false);

					// Auto login
					handleLoginSuccess(res.data);

					// Brief success display then redirect
					setTimeout(() => {
						navigate('/dashboard/home');
					}, 1500);
				}
			} catch (error) {
				setLoading(false);
				console.error('Error accepting invitation:', error);
				setIsError(true);
				setErrorMessage(translate('user.userInvitation.invitationIncorrectOrExpired'));
				setShowDialog(true);
			}
		};

		acceptInvitation();
	}, [
		invitationToken,
		navigate,
		translate,
		setToken,
		setCompanyId,
		setUserId,
		setUserAccountType,
		setLanguage
	]);

	return (
		<div className="min-h-screen bg-linear-to-b from-background to-muted/30 flex items-center justify-center p-4">
			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.3 }}
				className="w-full max-w-md"
			>
				<Card className="border-none shadow-xl">
					<CardContent className="p-8">
						{/* Logo */}
						<div className="flex justify-center mb-8">
							<img src="/imgs/LOGO_VERDE_2.svg" alt="ARCA" className="h-12 w-auto" />
						</div>

						{/* Title */}
						<h1 className="text-2xl font-bold text-center text-foreground mb-2">
							{translate('user.acceptInvitation.title')}
						</h1>

						{/* Content */}
						<div className="flex flex-col items-center justify-center py-8">
							{loading ? (
								<>
									<div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
										<Loader2 className="w-8 h-8 text-primary animate-spin" />
									</div>
									<p className="text-sm text-muted-foreground text-center">
										{translate('user.userInvitation.validating')}
									</p>
								</>
							) : !isError ? (
								<>
									<motion.div
										initial={{ scale: 0 }}
										animate={{ scale: 1 }}
										transition={{ type: 'spring', damping: 15 }}
										className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4"
									>
										<CheckCircle className="w-8 h-8 text-primary" />
									</motion.div>
									<p className="text-sm text-muted-foreground text-center mb-2">
										{translate('user.userInvitation.success')}
									</p>
									<p className="text-xs text-muted-foreground/70 text-center">
										{translate('user.userInvitation.redirecting')}
									</p>
								</>
							) : null}
						</div>
					</CardContent>
				</Card>
			</motion.div>

			{/* Error Dialog */}
			<Dialog open={showDialog} onOpenChange={setShowDialog}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
								<XCircle className="w-5 h-5 text-destructive" />
							</div>
							<DialogTitle>{translate('common.error')}</DialogTitle>
						</div>
					</DialogHeader>
					<DialogDescription className="text-sm text-muted-foreground">
						{errorMessage}
					</DialogDescription>
					<DialogFooter>
						<Button onClick={() => navigate('/')} className="rounded-full gap-2">
							{translate('user.userInvitation.goToLogin')}
							<ArrowRight className="w-4 h-4" />
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default UserInvitationV2;
